import { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft } from 'lucide-react';
import { Mascot } from '@/components/Mascot';
import { useTelegram } from '@/hooks/useTelegram';
import { useUserStore } from '@/store/userStore';
import { supabase } from '@/lib/supabase';
import { requireSession } from '@/lib/ensureSession';
import {
  FULL_QUESTIONS, FREQUENCY_OPTIONS, STYLE_TEXT, DISCLAIMER, NORMS, scoreFull,
  type EatingProfile, type EatingScale, type EatingTestRow,
} from '@/lib/eatingProfile';
import { buildPortrait, START_BRIDGE } from '@/lib/dofiStart';
import { useDofiStart } from '@/hooks/useDofiStart';

// ════════════════════════════════════════════════════════════════
// Тест пищевого поведения отдельным экраном (22.09.2026) — ПОЛНЫЙ DEBQ,
// 33 вопроса. Лид-магнит: по ссылке ?startapp=test новичок проходит его
// внутри онбординга (embedded, onDone → расчёт нормы), остальные — на /test.
// Результат относительно норм и ВСЕГДА с подписью «это не диагноз».
// ════════════════════════════════════════════════════════════════

const SCALE_LABEL: Record<EatingScale, string> = {
  emotional: 'Эмоциональное',
  external: 'Внешнее',
  restrained: 'Ограничительное',
};

function ScaleBar({ scale, value }: { scale: EatingScale; value: number }) {
  const pct = ((value - 1) / 4) * 100;
  const normPct = ((NORMS[scale] - 1) / 4) * 100;
  const over = value - NORMS[scale] >= 0.5;
  return (
    <div>
      <div className="flex justify-between text-xs mb-1.5">
        <span className="text-ink">{SCALE_LABEL[scale]}</span>
        <span className={over ? 'text-emerald-glow' : 'text-ink-dim'}>
          {value.toFixed(1)} <span className="text-ink-dim">· обычно {NORMS[scale].toFixed(1)}</span>
        </span>
      </div>
      <div className="relative h-2 rounded-full bg-bg-elevated">
        <div
          className={`absolute inset-y-0 left-0 rounded-full ${over ? 'bg-emerald-glow' : 'bg-ink-dim/60'}`}
          style={{ width: `${Math.max(4, pct)}%` }}
        />
        <div className="absolute -top-1 -bottom-1 w-px bg-ink-muted" style={{ left: `${normPct}%` }} aria-hidden="true" />
      </div>
    </div>
  );
}

export default function EatingTest({ onDone }: { onDone?: () => void } = {}) {
  const embedded = !!onDone;
  const nav = useNavigate();
  const { haptic } = useTelegram();
  const { profile, setProfile } = useUserStore();
  const [step, setStep] = useState(-1); // -1 — вступление
  const [answers, setAnswers] = useState<number[]>([]);
  const [result, setResult] = useState<EatingProfile | null>(null);
  // Прошлое прохождение — для «что изменилось» (история: eating_test_results)
  const [prev, setPrev] = useState<EatingTestRow | null>(null);
  const { startAt, start } = useDofiStart();
  const [starting, setStarting] = useState(false);
  async function beginStart() {
    setStarting(true);
    haptic('success');
    await start();
    setStarting(false);
    if (embedded) onDone!(); else nav('/');
  }
  useEffect(() => {
    if (!profile?.id) return;
    supabase
      .from('eating_test_results')
      .select('id,version,emotional,external,restrained,style,taken_at')
      .eq('user_id', profile.id)
      .order('taken_at', { ascending: false })
      .limit(1)
      .then(({ data }) => { if (data && data[0]) setPrev(data[0] as EatingTestRow); });
  }, [profile?.id]);

  async function answer(v: number) {
    haptic('light');
    const next = [...answers];
    next[step] = v;
    setAnswers(next);
    if (step < FULL_QUESTIONS.length - 1) {
      setStep(step + 1);
      return;
    }
    const r = scoreFull(next);
    setResult(r);
    haptic('success');
    // Сохраняем молча: не вышло — результат на экране всё равно есть.
    if (profile?.id && (await requireSession())) {
      // Каждое прохождение — в историю: первый результат не затирается.
      const { error: histErr } = await supabase.from('eating_test_results').insert({
        user_id: profile.id, version: r.version, emotional: r.emotional, external: r.external,
        restrained: r.restrained, style: r.style, answers: r.answers, taken_at: r.taken_at,
      });
      if (histErr) console.warn('[EatingTest] history save failed:', histErr);
      const { data, error } = await supabase
        .from('profiles')
        .update({ eating_profile: r })
        .eq('id', profile.id)
        .select()
        .single();
      if (error) console.warn('[EatingTest] save failed:', error);
      else if (data) setProfile(data);
    }
  }

  function discuss() {
    const title = result ? STYLE_TEXT[result.style].title.toLowerCase() : 'мой результат';
    try { sessionStorage.setItem('slava_prefill', `У меня по тесту ${title}. Что это значит для меня и с чего начать?`); } catch {}
    nav('/chat');
  }

  const st = result ? STYLE_TEXT[result.style] : null;
  const portrait = result ? buildPortrait(result, profile?.sex as any) : null;
  const q = step >= 0 && !result ? FULL_QUESTIONS[step] : null;

  return (
    <div className="max-w-md mx-auto px-5 pt-4 pb-28 min-h-screen flex flex-col">
      {/* В онбординге «назад» есть только между вопросами: уйти из него некуда. */}
      {(!embedded || (step > 0 && !result)) ? (
        <button
          type="button"
          onClick={() => (step > 0 && !result ? setStep(step - 1) : nav(-1))}
          className="self-start flex items-center gap-1 text-sm text-ink-muted mb-4"
        >
          <ChevronLeft size={18} /> Назад
        </button>
      ) : <div className="h-9" />}

      <AnimatePresence mode="wait">
        {step === -1 && (
          <motion.div key="intro" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="flex-1 flex flex-col">
            <Mascot pose="wave" className="w-32 h-32 mx-auto mb-4" />
            <h1 className="font-display text-2xl font-bold text-center text-balance mb-3">Какой у тебя тип пищевого поведения?</h1>
            <p className="text-sm text-ink-muted text-center leading-relaxed mb-2">
              33 вопроса, 4–5 минут. Узнаешь, что чаще всего влияет на то, как ты ешь: чувства, обстановка или запреты.
            </p>
            <p className="text-xs text-ink-dim text-center mb-8">
              Голландский опросник пищевого поведения (DEBQ). Отвечай про то, как обычно бывает, а не как хотелось бы. {DISCLAIMER}
            </p>
            <button
              type="button"
              onClick={() => { haptic('light'); setStep(0); }}
              className="w-full mt-auto py-3.5 rounded-xl bg-emerald-glow text-bg font-display font-bold active:scale-[0.98] transition-all"
            >
              Начать
            </button>
          </motion.div>
        )}

        {q && (
          <motion.div key={`q${step}`} initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -16 }} className="flex-1 flex flex-col">
            <div className="h-1 rounded-full bg-bg-elevated mb-6 overflow-hidden" aria-hidden="true">
              <div className="h-full bg-emerald-glow transition-all duration-300" style={{ width: `${((step + 1) / FULL_QUESTIONS.length) * 100}%` }} />
            </div>
            <div className="text-xs text-ink-dim mb-2">Вопрос {step + 1} из {FULL_QUESTIONS.length}</div>
            <h2 className="font-display text-xl font-bold leading-snug text-balance mb-6">{q.text}</h2>
            <div className="space-y-2">
              {FREQUENCY_OPTIONS.map((o) => (
                <button
                  key={o.value}
                  type="button"
                  onClick={() => answer(Number(o.value))}
                  className={`w-full py-3 px-4 rounded-xl border text-left text-sm font-medium transition-all active:scale-[0.99] ${
                    answers[step] === Number(o.value) ? 'border-emerald-glow/60 bg-emerald-glow/10' : 'border-line bg-bg-card hover:border-emerald-glow/40'
                  }`}
                >
                  {o.label}
                </button>
              ))}
            </div>
          </motion.div>
        )}

        {result && st && (
          <motion.div key="result" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="flex-1 flex flex-col">
            <Mascot pose={result.style === 'balanced' ? 'celebrate' : 'support'} className="w-28 h-28 mx-auto mb-3" />
            <div className="text-[11px] uppercase tracking-[0.2em] text-emerald-glow text-center mb-2">Твой результат</div>
            <h1 className="font-display text-2xl font-bold text-center text-balance mb-2">{portrait!.title}</h1>
            {portrait!.intensity && <p className="text-xs text-ink-dim text-center mb-4">{portrait!.intensity}</p>}
            {/* Портрет (30.10.2026): из ОТВЕТОВ человека, а не общими словами — см. lib/dofiStart */}
            <div className="space-y-3 mb-5">
              {portrait!.paragraphs.map((t, i) => (
                <p key={i} className="text-[15px] text-ink leading-relaxed">{t}</p>
              ))}
            </div>
            <div className="card p-4 space-y-4 mb-3">
              <ScaleBar scale="emotional" value={result.emotional} />
              <ScaleBar scale="external" value={result.external} />
              <ScaleBar scale="restrained" value={result.restrained} />
              <div className="text-[10px] text-ink-dim">Черта на шкале — средний балл у людей с нормальным весом.</div>
            </div>
            <p className="text-xs text-ink-muted text-center mb-6">{DISCLAIMER}</p>

            {/* Что изменилось с прошлого раза — ради этого тест и повторяют */}
            {prev && (
              <div className="card p-4 mb-5">
                <div className="text-sm font-medium mb-3">
                  Что изменилось с {format(new Date(prev.taken_at), 'd MMMM', { locale: ru })}
                  {prev.version === 1 && <span className="text-ink-dim font-normal"> (тогда была короткая версия)</span>}
                </div>
                <div className="space-y-2">
                  {(['emotional', 'external', 'restrained'] as EatingScale[]).map((sc) => {
                    const was = Number(prev[sc]);
                    const now = result[sc];
                    const d = Math.round((now - was) * 10) / 10;
                    return (
                      <div key={sc} className="flex items-center justify-between text-sm">
                        <span className="text-ink-muted">{SCALE_LABEL[sc]}</span>
                        <span className="tabular-nums">
                          {was.toFixed(1)} → <b>{now.toFixed(1)}</b>
                          <span className={`ml-2 text-xs ${d < 0 ? 'text-emerald-glow' : d > 0 ? 'text-warn' : 'text-ink-dim'}`}>
                            {d === 0 ? 'без изменений' : d < 0 ? `ниже на ${Math.abs(d).toFixed(1)}` : `выше на ${d.toFixed(1)}`}
                          </span>
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Мостик к действию: не ярлык, а «я помогу начать» (решение Славы 30.10.2026) */}
            <div className="rounded-2xl border border-emerald-glow/30 bg-emerald-glow/5 p-4 mb-5 flex gap-3 items-start">
              <Mascot pose="calm" compact float={false} className="w-12 h-12 shrink-0" />
              <p className="text-sm leading-relaxed text-ink">
                {START_BRIDGE[result.style]}
                {embedded ? ' Сначала посчитаем твою норму — это минута.' : ''}
              </p>
            </div>
            <div className="mt-auto space-y-2">
              {!startAt ? (
                <button
                  type="button"
                  onClick={beginStart}
                  disabled={starting}
                  className="w-full py-3.5 rounded-xl bg-emerald-glow text-bg font-display font-bold active:scale-[0.98] transition-all disabled:opacity-60"
                >
                  {starting ? '…' : 'Начать 3 дня с Дофи'}
                </button>
              ) : !embedded ? (
                <button
                  type="button"
                  onClick={discuss}
                  className="w-full py-3.5 rounded-xl bg-emerald-glow text-bg font-display font-bold active:scale-[0.98] transition-all"
                >
                  Обсудить результат с Дофи
                </button>
              ) : null}
              {embedded ? (
                (startAt ? (
                  <button type="button" onClick={() => { haptic('light'); onDone!(); }}
                    className="w-full py-3.5 rounded-xl bg-emerald-glow text-bg font-display font-bold active:scale-[0.98] transition-all">
                    Дальше — посчитать мою норму
                  </button>
                ) : (
                  <button type="button" onClick={() => { haptic('light'); onDone!(); }} className="w-full py-3 text-sm text-ink-muted">
                    Пока просто посчитать норму
                  </button>
                ))
              ) : (
                <>
                  {!startAt && (
                    <button type="button" onClick={discuss} className="w-full py-3 text-sm text-ink-muted">
                      Сначала обсудить с Дофи
                    </button>
                  )}
                  <button type="button" onClick={() => nav('/')} className="w-full py-3 text-sm text-ink-muted">
                    На главную
                  </button>
                </>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
